import { useNetworkState } from '@uidotdev/usehooks';
import { useMemo } from 'react';
import { logger } from '@/utils/logger';

enum NetworkStatus {
  OFFLINE = 'OFFLINE',
  UNSTABLE = 'UNSTABLE',
  ONLINE = 'ONLINE',
}

enum EffectiveConnectionType {
  SLOW_2G = 'slow-2g',
  TWO_G = '2g',
  THREE_G = '3g',
}

const RTT_LIMIT = 500;
const DOWNLINK_LIMIT = 1.5;

export { NetworkStatus };

export const useNetworkStatus = (): { status: NetworkStatus } => {
  const state = useNetworkState();

  const status: NetworkStatus = useMemo(() => {
    if (!state.online) {
      logger.warn('[NetworkStatus] Network status detected as offline', {
        networkStatus: NetworkStatus.OFFLINE,
      });
      return NetworkStatus.OFFLINE;
    }

    if (
      [EffectiveConnectionType.SLOW_2G, EffectiveConnectionType.TWO_G, EffectiveConnectionType.THREE_G].includes(
        state.effectiveType as EffectiveConnectionType
      ) ||
      (state.rtt && state.rtt > RTT_LIMIT) ||
      (state.downlink && state.downlink < DOWNLINK_LIMIT)
    ) {
      logger.warn('[NetworkStatus] Network status detected as unstable', {
        networkStatus: NetworkStatus.UNSTABLE,
        effectiveType: state.effectiveType,
        rtt: state.rtt,
        downlink: state.downlink,
      });
      return NetworkStatus.UNSTABLE;
    }

    return NetworkStatus.ONLINE;
  }, [state.online, state.effectiveType, state.rtt, state.downlink]);

  return { status };
};
